import React, { useState, useCallback } from 'react';
import { useChatState, useChatActions } from '../core/ChatContext';
import { Send } from 'lucide-react';
import { cn } from '@/lib/utils';

export const ChatInput: React.FC = () => {
  const { state } = useChatState();
  const { sendMessage, clearMessages, isLoading } = useChatActions();
  const [inputValue, setInputValue] = useState('');

  const handleSendMessage = useCallback(async () => {
    if (!inputValue.trim() || isLoading || state.isTyping) return;
    
    const message = inputValue.trim();
    setInputValue('');
    
    try {
      await sendMessage(message);
    } catch (error) {
      console.error('Failed to send message:', error);
    }
  }, [inputValue, isLoading, state.isTyping, sendMessage]);

  const handleKeyPress = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  }, [handleSendMessage]);

  const isDisabled = isLoading || state.isTyping;

  return (
    <div className="nm-p-lg border-t border-white/20 nm-surface-elevated">
      <div className="flex gap-3 items-end">
        <div className="flex-1">
          <textarea
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask me anything about this lesson..."
            className={cn(
              "nm-input w-full nm-rounded-xl nm-text-primary",
              "resize-none nm-transition-normal",
              "text-sm min-h-[44px] max-h-[120px]",
              isDisabled && "opacity-50 cursor-not-allowed"
            )}
            rows={1}
            disabled={isDisabled}
            style={{
              height: 'auto',
              overflow: 'hidden'
            }}
            onInput={(e) => {
              const target = e.target as HTMLTextAreaElement;
              target.style.height = 'auto';
              target.style.height = target.scrollHeight + 'px';
            }}
          />
        </div>
        
        <button
          onClick={handleSendMessage}
          disabled={!inputValue.trim() || isDisabled}
          className={cn(
            "nm-button nm-button-primary w-11 h-11 nm-rounded-xl flex items-center justify-center nm-interactive",
            isDisabled && "opacity-50 cursor-not-allowed"
          )}
        >
          <Send className="w-4 h-4" />
        </button>
      </div>
      
      {/* Neumorphic Footer with controls */}
      <div className="flex items-center justify-between nm-m-md text-xs nm-text-muted">
        <span>Press Enter to send, Shift+Enter for new line</span>
        {state.messages.length > 0 && (
          <button
            onClick={clearMessages}
            className="nm-text-accent hover:nm-text-primary font-medium nm-interactive transition-colors"
          >
            Clear Chat
          </button>
        )}
      </div>

      {/* Neumorphic Loading indicator */}
      {isLoading && (
        <div
          className="flex items-center gap-2 nm-m-sm text-xs nm-text-secondary nm-animate-pulse"
        >
          <div className="flex gap-1">
            <div
              className="w-1 h-1 nm-surface-purple nm-rounded-full nm-animate-pulse"
              style={{ animationDelay: '0s' }}
            />
            <div
              className="w-1 h-1 nm-surface-purple nm-rounded-full nm-animate-pulse"
              style={{ animationDelay: '0.2s' }}
            />
            <div
              className="w-1 h-1 nm-surface-purple nm-rounded-full nm-animate-pulse"
              style={{ animationDelay: '0.4s' }}
            />
          </div>
          <span>Sending...</span>
        </div>
      )}
    </div>
  );
};